import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, money, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  CheckCircle2, Flag, Paperclip, History as HistoryIcon, AlarmClock, Trash2, Pencil,
  Download, Eye, Loader2, Plus, MessageSquare, CalendarDays, RefreshCw, Banknote,
  FileText, ClipboardCheck, LayoutList, Upload, Ban, Lock
} from "lucide-react";

export const WRITE_ROLES = ["amden_super_admin", "ops_owner", "scheduler_admin"];
const MANAGER_ROLES = ["amden_super_admin", "ops_owner"];
const BILLING_EDIT_LOCK = ["Awaiting Approval", "Ready for Invoicing", "Invoiced", "Paid", "Closed"];
const JOB_TYPES = ["Planned preventative maintenance", "Reactive call-out", "Emergency call-out", "Installation", "Remedial work", "Inspection", "Survey", "Quotation visit", "Project work", "Return visit"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const OP_STATUSES = ["New", "Awaiting Review", "Awaiting Purchase Order", "Awaiting Materials", "Ready to Schedule", "Scheduled", "Assigned", "Technician En Route", "In Progress", "Paused", "Completed on Site", "Awaiting Documentation", "Awaiting Manager Approval", "Further Work Required", "Completed", "Closed", "Cancelled"];
const fmtDT = (v) => (v ? new Date(v).toLocaleString([], { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

// ---------------------------------------------------------------------------
// SLA badge for lists / boards / calendars
// ---------------------------------------------------------------------------
export function SlaBadge({ job, withTime = false }) {
  if (!job?.sla_active) return null;
  const res = job.sla_result;
  const dl = job.sla_deadline ? new Date(job.sla_deadline) : null;
  const hrs = dl ? (dl.getTime() - Date.now()) / 3600000 : null;
  let chip = null;
  if (res === "Achieved") chip = { cls: "bg-emerald-100 text-emerald-700", label: "SLA met" };
  else if (res === "Breached") chip = { cls: "bg-red-100 text-red-700", label: "SLA breached" };
  else if (hrs !== null && hrs < 0) chip = { cls: "bg-red-600 text-white animate-pulse", label: "SLA OVERDUE" };
  else if (hrs !== null && hrs <= 24) chip = { cls: "bg-red-100 text-red-700 font-bold", label: `SLA ${Math.max(0, Math.floor(hrs))}h left` };
  else if (dl) chip = { cls: "bg-amber-100 text-amber-700", label: `SLA due ${fmtDate(job.sla_deadline)}` };
  return (
    <span className="inline-flex items-center gap-1 flex-shrink-0" data-testid={`sla-badge-${job.id}`}>
      <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white"><AlarmClock className="h-3 w-3" />SLA</span>
      {withTime && chip && <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${chip.cls}`}>{chip.label}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// SLA form fields (create dialog + SLA tab)
// ---------------------------------------------------------------------------
export function SlaFields({ form, setForm }) {
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <div className="border border-red-200 bg-red-50/60 rounded-md p-3 space-y-3" data-testid="sla-fields">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-red-800 flex items-center gap-1.5"><AlarmClock className="h-4 w-4" />SLA Job</Label>
        <Switch data-testid="sla-active-switch" checked={!!form.sla_active} onCheckedChange={(v) => set("sla_active", v)} />
      </div>
      {form.sla_active && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {[["sla_response_hours", "Response (hrs)"], ["sla_attendance_hours", "Attendance (hrs)"], ["sla_completion_hours", "Completion (hrs)"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs text-slate-600">{l}</Label>
                <Input data-testid={`field-${k}`} type="number" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? null : parseFloat(e.target.value))} className="mt-1 bg-white" /></div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-slate-600">SLA start</Label>
              <Input data-testid="field-sla_start" type="datetime-local" value={(form.sla_start || "").slice(0, 16)} onChange={(e) => set("sla_start", e.target.value)} className="mt-1 bg-white" /></div>
            <div><Label className="text-xs text-slate-600">Priority level</Label>
              <Select value={form.sla_priority || ""} onValueChange={(v) => set("sla_priority", v)}>
                <SelectTrigger data-testid="field-sla_priority" className="mt-1 bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-white">{["Low", "Medium", "High", "Critical"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-xs text-slate-600">SLA instructions</Label>
            <Textarea data-testid="field-sla_instructions" value={form.sla_instructions || ""} onChange={(e) => set("sla_instructions", e.target.value)} className="mt-1 bg-white" rows={2} /></div>
          <p className="text-[11px] text-red-700/70">Deadline is calculated automatically: SLA start + completion timeframe. Scheduling past the deadline will be blocked with a warning.</p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flagged notes
// ---------------------------------------------------------------------------
function FlaggedNotes({ detail, setDetail, canWrite, onChanged }) {
  const [text, setText] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const apply = (data, msg) => { setDetail((p) => ({ ...p, flagged_notes: data.flagged_notes })); onChanged(); toast.success(msg); };
  const add = async () => {
    if (!text.trim()) return toast.error("Write the note first");
    try { const { data } = await api.post(`/jobs/${detail.id}/flagged-notes`, { text }); apply(data, "Flagged note added"); setText(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const save = async (nid) => {
    try { const { data } = await api.put(`/jobs/${detail.id}/flagged-notes/${nid}`, { text: editText }); apply(data, "Flagged note updated"); setEditId(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (nid) => {
    try { const { data } = await api.delete(`/jobs/${detail.id}/flagged-notes/${nid}`); apply(data, "Flagged note removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const notes = detail.flagged_notes || [];
  return (
    <div className="space-y-3" data-testid="flagged-notes-panel">
      <p className="text-xs text-slate-500">Critical instructions shown prominently to the assigned engineer — access codes, contacts, opening hours, parking, security.</p>
      {notes.length === 0 && <p className="text-sm text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded-md">No flagged notes on this job.</p>}
      {notes.map((n) => (
        <div key={n.id} data-testid={`flagged-note-${n.id}`} className="border-2 border-amber-300 bg-amber-50 rounded-md p-3">
          <div className="flex items-start gap-2">
            <Flag className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              {editId === n.id ? (
                <div className="space-y-2">
                  <Textarea data-testid={`flagged-note-edit-input-${n.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} className="bg-white" rows={3} />
                  <div className="flex gap-2">
                    <Button size="sm" className="btn-accent" data-testid={`flagged-note-save-${n.id}`} onClick={() => save(n.id)}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-amber-900 whitespace-pre-wrap">{n.text}</p>
                  <p className="text-[10px] text-amber-600 mt-1.5">
                    Created by {n.created_by} · {fmtDT(n.created_at)}
                    {n.updated_by && <> · Updated by {n.updated_by} · {fmtDT(n.updated_at)}</>}
                  </p>
                </>
              )}
            </div>
            {canWrite && editId !== n.id && (
              <div className="flex gap-1 flex-shrink-0">
                <button data-testid={`flagged-note-edit-${n.id}`} onClick={() => { setEditId(n.id); setEditText(n.text); }} className="text-amber-500 hover:text-amber-800"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`flagged-note-delete-${n.id}`} onClick={() => del(n.id)} className="text-amber-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>
        </div>
      ))}
      {canWrite && (
        <div className="border-t border-slate-100 pt-3 space-y-2">
          <Label className="text-xs text-slate-600">Add flagged note</Label>
          <Textarea data-testid="flagged-note-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Gate code 4482#, contact Maria 087…, opening hours 08:00–18:00" rows={2} />
          <Button size="sm" className="btn-accent" data-testid="flagged-note-add-btn" onClick={add}><Flag className="h-3.5 w-3.5 mr-1" />Add flagged note</Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// History timeline
// ---------------------------------------------------------------------------
const H_ICONS = { status: RefreshCw, schedule: CalendarDays, note: MessageSquare, billing: Banknote, file: Paperclip, flagged_note: Flag, quote: FileText, report: ClipboardCheck, created: Plus, completion: CheckCircle2, sla: AlarmClock, edit: Pencil, cancel: Ban, restore: RefreshCw };
const H_COLORS = { status: "text-sky-600 bg-sky-50", schedule: "text-indigo-600 bg-indigo-50", note: "text-slate-600 bg-slate-100", billing: "text-purple-600 bg-purple-50", file: "text-teal-600 bg-teal-50", flagged_note: "text-amber-600 bg-amber-50", quote: "text-purple-600 bg-purple-50", report: "text-emerald-600 bg-emerald-50", created: "text-slate-600 bg-slate-100", completion: "text-emerald-600 bg-emerald-50", sla: "text-red-600 bg-red-50", edit: "text-orange-600 bg-orange-100", cancel: "text-orange-600 bg-orange-100", restore: "text-orange-600 bg-orange-100" };
const H_ROW = { edit: "bg-orange-50 border border-orange-200 rounded-md px-2 -mx-2", cancel: "bg-orange-50 border border-orange-200 rounded-md px-2 -mx-2", restore: "bg-orange-50 border border-orange-200 rounded-md px-2 -mx-2" };

function History({ jobId, canWrite }) {
  const [items, setItems] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/jobs/${jobId}/history`).then((r) => setItems(r.data)).catch(() => setItems([])), [jobId]);
  useEffect(() => { load(); }, [load]);
  const addNote = async () => {
    if (!note.trim()) return toast.error("Write the note first");
    try { await api.post(`/jobs/${jobId}/notes`, { text: note }); setNote(""); load(); toast.success("Note added to history"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="job-history-panel">
      {canWrite && (
        <div className="flex gap-2">
          <Input data-testid="job-add-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note to this job's history…" onKeyDown={(e) => e.key === "Enter" && addNote()} />
          <Button size="sm" className="btn-accent flex-shrink-0" data-testid="job-add-note-btn" onClick={addNote}><MessageSquare className="h-3.5 w-3.5 mr-1" />Add note</Button>
        </div>
      )}
      {items === null && <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>}
      {items && items.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No history recorded yet.</p>}
      <div className="relative space-y-0">
        {(items || []).map((h, i) => {
          const Icon = H_ICONS[h.type] || LayoutList;
          const cls = H_COLORS[h.type] || "text-slate-600 bg-slate-100";
          return (
            <div key={h.id} data-testid={`history-item-${h.id}`} className={`flex gap-3 pb-4 relative ${H_ROW[h.type] || ""}`}>
              {i < items.length - 1 && <span className="absolute left-[13px] top-7 bottom-0 w-px bg-slate-200" />}
              <span className={`h-7 w-7 rounded-full grid place-items-center flex-shrink-0 ${cls}`}><Icon className="h-3.5 w-3.5" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{h.summary}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{fmtDT(h.at)} · {h.user_name} · <span className="uppercase tracking-wide">{(h.type || "").replace("_", " ")}</span></p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------
const PREVIEWABLE = ["pdf", "png", "jpg", "jpeg", "webp", "gif"];
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function Files({ jobId, canWrite }) {
  const [files, setFiles] = useState(null);
  const [engVisible, setEngVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(() => api.get(`/jobs/${jobId}/files`).then((r) => setFiles(r.data)).catch(() => setFiles([])), [jobId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("engineer_visible", engVisible ? "true" : "false");
    setUploading(true);
    try { await api.post(`/jobs/${jobId}/files`, fd); toast.success(`${f.name} uploaded`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const fetchBlob = async (f) => {
    const res = await api.get(`/jobs/${jobId}/files/${f.id}/download`, { responseType: "blob" });
    return URL.createObjectURL(new Blob([res.data], { type: f.content_type }));
  };
  const download = async (f) => {
    const url = await fetchBlob(f);
    const a = document.createElement("a"); a.href = url; a.download = f.name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };
  const preview = async (f) => {
    const url = await fetchBlob(f);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const del = async (f) => {
    try { await api.delete(`/jobs/${jobId}/files/${f.id}`); toast.success("File deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="job-files-panel">
      {canWrite && (
        <div className="border border-dashed border-slate-300 rounded-md p-3 space-y-2 bg-slate-50/60">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className={`inline-flex items-center gap-2 text-sm font-medium accent-text cursor-pointer ${uploading ? "opacity-50" : ""}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Upload file (PDF, docs, images — max 15 MB)"}
              <input data-testid="job-file-input" type="file" className="hidden" onChange={upload} disabled={uploading}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.gif,.heic,.ppt,.pptx" />
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <Switch data-testid="job-file-engineer-visible" checked={engVisible} onCheckedChange={setEngVisible} />
              Engineer can view
            </label>
          </div>
        </div>
      )}
      {files === null && <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>}
      {files && files.length === 0 && <p className="text-sm text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded-md">No files on this job yet.</p>}
      {(files || []).map((f) => (
        <div key={f.id} data-testid={`job-file-${f.id}`} className="flex items-center gap-3 border border-slate-200 rounded-md p-2.5 bg-white">
          <span className="h-9 w-9 rounded-md bg-slate-100 grid place-items-center flex-shrink-0"><Paperclip className="h-4 w-4 text-slate-500" /></span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">{f.name}</div>
            <div className="text-[11px] text-slate-400">
              {(f.ext || "").toUpperCase()} · {fmtSize(f.size)} · {f.uploaded_by} · {fmtDT(f.uploaded_at)}
              {f.engineer_visible && <span className="ml-1.5 text-teal-600 font-medium">· visible to engineer</span>}
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            {PREVIEWABLE.includes(f.ext) && <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`job-file-preview-${f.id}`} onClick={() => preview(f)} title="Preview"><Eye className="h-4 w-4" /></Button>}
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`job-file-download-${f.id}`} onClick={() => download(f)} title="Download"><Download className="h-4 w-4" /></Button>
            {canWrite && <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`job-file-delete-${f.id}`} onClick={() => del(f)} title="Delete"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SLA panel (tab)
// ---------------------------------------------------------------------------
function SlaPanel({ detail, patch, canWrite }) {
  const pick = (d) => ({
    sla_active: !!d.sla_active, sla_response_hours: d.sla_response_hours, sla_attendance_hours: d.sla_attendance_hours,
    sla_completion_hours: d.sla_completion_hours, sla_start: d.sla_start, sla_priority: d.sla_priority,
    sla_instructions: d.sla_instructions,
  });
  const [form, setForm] = useState(pick(detail));
  const detailKey = `${detail.id}|${detail.sla_deadline}|${detail.sla_active}`;
  useEffect(() => { setForm(pick(detail)); }, [detailKey]);
  const save = () => patch({ ...form }, "SLA settings saved");
  return (
    <div className="space-y-3" data-testid="sla-panel">
      {detail.sla_active && (
        <div className="flex items-center gap-3 flex-wrap bg-slate-50 rounded-md p-3">
          <SlaBadge job={detail} withTime />
          {detail.sla_deadline && <span className="text-sm text-slate-700">Deadline: <b>{fmtDT(detail.sla_deadline)}</b></span>}
          {detail.sla_result && <StatusBadge status={detail.sla_result} testid="sla-result-badge" />}
        </div>
      )}
      {canWrite ? (
        <>
          <SlaFields form={form} setForm={setForm} />
          <Button size="sm" className="btn-accent" data-testid="sla-save-btn" onClick={save}>Save SLA settings</Button>
        </>
      ) : (
        <div className="text-sm text-slate-600 space-y-1">
          {detail.sla_instructions && <p><b>Instructions:</b> {detail.sla_instructions}</p>}
          <p>Response {detail.sla_response_hours || "—"}h · Attendance {detail.sla_attendance_hours || "—"}h · Completion {detail.sla_completion_hours || "—"}h</p>
        </div>
      )}
      {(detail.sla_history || []).length > 0 && (
        <div className="border-t border-slate-100 pt-3">
          <h4 className="font-head font-semibold text-sm mb-2">SLA history</h4>
          <div className="space-y-1.5">
            {[...detail.sla_history].reverse().map((h, i) => (
              <div key={i} className="text-xs text-slate-600 flex gap-2">
                <span className="text-slate-400 whitespace-nowrap">{fmtDT(h.at)}</span>
                <span>{h.change} <span className="text-slate-400">— {h.by}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main job detail dialog
// ---------------------------------------------------------------------------
export default function JobDetail({ job, onClose, onChanged, techs = [], cur = "EUR" }) {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const canApprove = MANAGER_ROLES.includes(user?.role);
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [detail, setDetail] = useState(job);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  useEffect(() => { setDetail(job); }, [job]);

  const billingLocked = BILLING_EDIT_LOCK.includes(job?.billing_status) || BILLING_EDIT_LOCK.includes(detail?.billing_status);
  const isCompleted = ["Completed", "Closed"].includes(detail?.operational_status);
  const isCancelled = detail?.operational_status === "Cancelled";
  const canEditContent = canWrite && !billingLocked && (!isCompleted || isManager);

  const patch = async (p, msg) => {
    try {
      const { data } = await api.put(`/jobs/${detail.id}`, p);
      setDetail(data); onChanged();
      if (msg) toast.success(msg);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(e.response.data?.detail, {
          duration: 12000,
          action: { label: "Override & save", onClick: () => patch({ ...p, sla_override: true }, msg) },
        });
      } else toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  const approve = async () => {
    try { await api.post(`/jobs/${detail.id}/approve`); const { data } = await api.get(`/jobs/${detail.id}`); setDetail(data); onChanged(); toast.success("Job approved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };
  const openEdit = () => {
    setEditForm({
      title: detail.title || "", job_type: detail.job_type || "", priority: detail.priority || "",
      description: detail.description || "", po_number: detail.po_number || "",
      scheduled_date: (detail.scheduled_date || "").slice(0, 10), completion_date: (detail.completion_date || "").slice(0, 10),
      tech_notes: detail.tech_notes || "", service_report_complete: !!detail.service_report_complete,
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editForm.title?.trim()) return toast.error("Job title is required");
    const payload = { ...editForm };
    if (!payload.scheduled_date) delete payload.scheduled_date;
    if (!payload.completion_date) delete payload.completion_date;
    await patch(payload, isCompleted ? "Completed job updated — highlighted on the audit trail" : "Job details updated");
    setEditOpen(false);
  };
  const doCancel = async () => {
    if (!cancelReason.trim()) return toast.error("A cancellation reason is required");
    try {
      const { data } = await api.post(`/jobs/${detail.id}/cancel`, { reason: cancelReason.trim() });
      setDetail(data); onChanged(); setCancelOpen(false); setCancelReason("");
      toast.success("Job cancelled — removed from schedule and pipeline");
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };
  const doRestore = async () => {
    try {
      const { data } = await api.post(`/jobs/${detail.id}/restore`);
      setDetail(data); onChanged();
      toast.success(`Job restored — back to '${data.operational_status}'`);
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  if (!detail) return null;
  const flaggedCount = (detail.flagged_notes || []).length;

  return (
    <Dialog open={!!job} onOpenChange={(v) => !v && onClose()}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="job-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2 flex-wrap">
            {detail.ref} · {detail.title}
            <SlaBadge job={detail} withTime />
            <StatusBadge status={detail.operational_status} /><StatusBadge status={detail.billing_status} />
            {detail.auto_billing && <span className="text-[10px] bg-purple-100 text-purple-700 font-semibold rounded px-1.5 py-0.5">AUTO BILLING</span>}
          </DialogTitle>
          <DialogDescription className="sr-only">Job detail — overview, flagged notes, history, files and SLA.</DialogDescription>
        </DialogHeader>

        {flaggedCount > 0 && (
          <div className="border-2 border-amber-300 bg-amber-50 rounded-md px-3 py-2 flex items-center gap-2 text-sm text-amber-900" data-testid="job-flagged-banner">
            <Flag className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <b>{flaggedCount} flagged note{flaggedCount > 1 ? "s" : ""}</b> — critical instructions on this job. See the Flagged tab.
          </div>
        )}

        <Tabs defaultValue="overview">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="job-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="flagged" data-testid="job-tab-flagged" className={flaggedCount ? "text-amber-700" : ""}><Flag className="h-3.5 w-3.5 mr-1" />Flagged ({flaggedCount})</TabsTrigger>
            <TabsTrigger value="history" data-testid="job-tab-history"><HistoryIcon className="h-3.5 w-3.5 mr-1" />History</TabsTrigger>
            <TabsTrigger value="files" data-testid="job-tab-files"><Paperclip className="h-3.5 w-3.5 mr-1" />Files</TabsTrigger>
            <TabsTrigger value="sla" data-testid="job-tab-sla" className={detail.sla_active ? "text-red-600" : ""}><AlarmClock className="h-3.5 w-3.5 mr-1" />SLA</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm bg-slate-50 rounded-md p-3">
              {[["Customer", detail.customer_name], ["Site", detail.site_name], ["Type", detail.job_type], ["Priority", detail.priority], ["Scheduled", fmtDate(detail.scheduled_date)], ["Technician", detail.technician_name], ["PO Number", detail.po_number], ["Completion", fmtDate(detail.completion_date)], ["Expected Value", money(detail.expected_value, cur)],
                ["Quotation", detail.quotation_ref, detail.quotation_id && `/app/quotations?open=${detail.quotation_id}`],
                ["Contract", detail.contract_ref, detail.contract_id && `/app/contracts?open=${detail.contract_id}`],
                ["Project", detail.project_ref, detail.project_id && `/app/projects?open=${detail.project_id}`]].map(([k, v, to]) => (
                <div key={k}><div className="text-xs text-slate-400">{k}</div>
                  <div className="text-slate-800">{v && to ? <Link to={to} data-testid={`job-link-${k.toLowerCase()}`} className="accent-text hover:underline">{v}</Link> : (v || "—")}</div></div>
              ))}
            </div>
            {detail.description && <p className="text-sm text-slate-600 whitespace-pre-wrap">{detail.description}</p>}

            {detail.billing_missing?.length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2.5">
                <b>Missing for billing:</b> {detail.billing_missing.join(", ")}
              </div>
            )}

            {billingLocked && (
              <div data-testid="job-billing-locked-banner" className="text-xs bg-slate-100 border border-slate-300 text-slate-700 rounded-md p-2.5 flex items-center gap-2">
                <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                <span><b>Locked</b> — this job is in billing ({detail.billing_status}). Editing and cancellation are no longer possible.</span>
              </div>
            )}
            {!billingLocked && isCompleted && isManager && (
              <div data-testid="job-completed-edit-banner" className="text-xs bg-orange-50 border border-orange-300 text-orange-800 rounded-md p-2.5 flex items-center gap-2">
                <Pencil className="h-3.5 w-3.5 flex-shrink-0" />
                <span><b>Completed job</b> — manager editing is enabled. Any change is highlighted in orange on the audit trail.</span>
              </div>
            )}
            {!billingLocked && isCompleted && !isManager && (
              <div data-testid="job-completed-readonly-banner" className="text-xs bg-slate-100 border border-slate-300 text-slate-600 rounded-md p-2.5 flex items-center gap-2">
                <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                <span>This job is completed — only a manager can edit it.</span>
              </div>
            )}
            {isCancelled && (
              <div data-testid="job-cancelled-banner" className="text-sm bg-orange-50 border-2 border-orange-300 text-orange-900 rounded-md p-3">
                <div className="flex items-center gap-2 font-semibold"><Ban className="h-4 w-4" />Job cancelled</div>
                <p className="text-xs mt-1">Reason: {detail.cancelled_reason || "—"} · by {detail.cancelled_by} · {fmtDT(detail.cancelled_at)}</p>
                {isManager && !billingLocked && (
                  <Button size="sm" variant="outline" className="mt-2 border-orange-300 text-orange-800 hover:bg-orange-100" data-testid="job-restore-btn" onClick={doRestore}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />Restore job
                  </Button>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-3">
              <div>
                <Label className="text-xs text-slate-600">Operational status</Label>
                <Select value={detail.operational_status} onValueChange={(v) => patch({ operational_status: v }, "Status updated")} disabled={!canEditContent}>
                  <SelectTrigger data-testid="job-op-status" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">{OP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Assign technician</Label>
                <Select value={detail.technician_id || ""} onValueChange={(v) => { const t = techs.find((x) => x.id === v); patch({ technician_id: v, technician_name: t?.name, operational_status: detail.operational_status === "New" ? "Assigned" : detail.operational_status }, "Technician assigned"); }} disabled={!canEditContent}>
                  <SelectTrigger data-testid="job-assign-tech" className="mt-1 bg-white"><SelectValue placeholder="Select technician" /></SelectTrigger>
                  <SelectContent className="bg-white">{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.source === "employee" && t.role_title ? ` — ${t.role_title}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border-t border-slate-100 pt-3">
              {[["Labour hours", "labour_hours"], ["Materials cost", "materials_cost"], ["Labour charge", "labour_charge"], ["Materials charge", "materials_charge"], ["Call-out charge", "callout_charge"], ["Travel charge", "travel_charge"]].map(([lbl, key]) => (
                <div key={key}><Label className="text-xs text-slate-600">{lbl}</Label>
                  <Input data-testid={`job-${key}`} type="number" defaultValue={detail[key] || ""} className="mt-1 bg-white" disabled={!canEditContent}
                    onBlur={(e) => patch({ [key]: parseFloat(e.target.value) || 0 })} /></div>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap border-t border-slate-100 pt-3">
              {isManager && !billingLocked && (
                <Button variant="outline" size="sm" data-testid="job-edit-btn" onClick={openEdit}>
                  <Pencil className="h-4 w-4 mr-1" />Edit job & sign-off
                </Button>
              )}
              <Button variant="outline" size="sm" data-testid="job-mark-report" onClick={() => patch({ service_report_complete: true }, "Service report marked complete")} disabled={detail.service_report_complete || !canEditContent}>
                <CheckCircle2 className="h-4 w-4 mr-1" />{detail.service_report_complete ? "Report complete" : "Mark report complete"}
              </Button>
              {canApprove && (
                <Button size="sm" className="btn-accent" data-testid="job-approve-btn" onClick={approve} disabled={detail.manager_approved || billingLocked || isCancelled}>
                  {detail.manager_approved ? `Approved by ${detail.approved_by}` : "Manager approve"}
                </Button>
              )}
              {isManager && !billingLocked && !isCancelled && (
                <Button variant="outline" size="sm" data-testid="job-cancel-btn" onClick={() => setCancelOpen(true)}
                  className="ml-auto border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800">
                  <Ban className="h-4 w-4 mr-1" />Cancel job
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="flagged" className="mt-4">
            <FlaggedNotes detail={detail} setDetail={setDetail} canWrite={canWrite} onChanged={onChanged} />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <History jobId={detail.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="files" className="mt-4">
            <Files jobId={detail.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="sla" className="mt-4">
            <SlaPanel detail={detail} patch={patch} canWrite={canEditContent} />
          </TabsContent>
        </Tabs>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-head">Edit job & sign-off · {detail.ref}</DialogTitle>
              {isCompleted && <DialogDescription className="text-xs text-orange-700">This job is completed — your changes will be highlighted in orange on the audit trail.</DialogDescription>}
            </DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-600">Job title</Label>
                <Input data-testid="edit-job-title" value={editForm.title || ""} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-600">Job type</Label>
                  <Select value={editForm.job_type || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, job_type: v }))}>
                    <SelectTrigger data-testid="edit-job-type" className="mt-1 bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-60">{JOB_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs text-slate-600">Priority</Label>
                  <Select value={editForm.priority || ""} onValueChange={(v) => setEditForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger data-testid="edit-job-priority" className="mt-1 bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div><Label className="text-xs text-slate-600">Description</Label>
                <Textarea data-testid="edit-job-description" value={editForm.description || ""} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" rows={3} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-600">PO number</Label>
                  <Input data-testid="edit-job-po" value={editForm.po_number || ""} onChange={(e) => setEditForm((f) => ({ ...f, po_number: e.target.value }))} className="mt-1" /></div>
                <div><Label className="text-xs text-slate-600">Scheduled date</Label>
                  <Input data-testid="edit-job-scheduled" type="date" value={editForm.scheduled_date || ""} onChange={(e) => setEditForm((f) => ({ ...f, scheduled_date: e.target.value }))} className="mt-1" /></div>
                <div><Label className="text-xs text-slate-600">Completion date</Label>
                  <Input data-testid="edit-job-completion" type="date" value={editForm.completion_date || ""} onChange={(e) => setEditForm((f) => ({ ...f, completion_date: e.target.value }))} className="mt-1" /></div>
              </div>
              <div className="border border-slate-200 rounded-md p-3 space-y-3 bg-slate-50/60">
                <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><ClipboardCheck className="h-3.5 w-3.5" />Sign-off</p>
                <div><Label className="text-xs text-slate-600">Engineer notes</Label>
                  <Textarea data-testid="edit-job-technotes" value={editForm.tech_notes || ""} onChange={(e) => setEditForm((f) => ({ ...f, tech_notes: e.target.value }))} className="mt-1 bg-white" rows={2} /></div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Switch data-testid="edit-job-report-complete" checked={!!editForm.service_report_complete} onCheckedChange={(v) => setEditForm((f) => ({ ...f, service_report_complete: v }))} />
                  Service report complete
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button className="btn-accent" data-testid="edit-job-save" onClick={saveEdit}>Save changes</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={cancelOpen} onOpenChange={(o) => { setCancelOpen(o); if (!o) setCancelReason(""); }}>
          <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2 text-orange-800"><Ban className="h-4 w-4" />Cancel job {detail.ref}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">The job will be removed from the schedule and pipeline. The record is kept and a manager can restore it. This is highlighted in orange on the audit trail.</DialogDescription>
            </DialogHeader>
            <div><Label className="text-xs text-slate-600">Reason for cancellation (required)</Label>
              <Textarea data-testid="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Customer postponed the works until next quarter" className="mt-1" rows={3} /></div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep job</Button>
              <Button data-testid="cancel-job-confirm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={doCancel} disabled={!cancelReason.trim()}>Cancel this job</Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
